import jsPDF from 'jspdf';
import { TylerState } from '../utils/TylerState';

// Define the interface locally since it's not exported from a types file
interface PurchaseInputs {
  analysisMode: 'housing-to-rent' | 'rent-to-housing' | 'compare' | 'landlord-breakeven';
  isLandlord: boolean;
  ownershipYears: number;
  houseCost: number;
  closingCostsPercent: number;
  downpaymentAmount: number;
  downpaymentPercent: number;
  useDownpaymentPercent: boolean;
  mortgageYears: number;
  interestRate: number;
  propertyTaxPercent: number;
  maintenancePercent: number;
  condoFees: number;
  annualAppreciation: number;
  sellingCostsPercent: number;
  taxesPercent: number;
  monthlyRent: number;
  propertyManagementFeePercent: number;
  vacancyRatePercent?: number;
  investmentReturnRate: number;
  rentalInflationRate: number;
  investmentTaxRate: number;
  marginalTaxRate?: number;
  renterSavingsDisciplinePercent?: number;
  inflationRate: number;
  region?: string;
  annualHomeInsurance?: number;
}

export class HousingPDFService {
  // Professional financial statement constants
  private readonly MARGIN_INCH = 25.4; // 1 inch in mm
  private readonly PAGE_WIDTH = 210; // A4 width in mm
  private readonly PAGE_HEIGHT = 297; // A4 height in mm

  private formatCurrency(amount: number | null | undefined): string {
    // Handle NaN, null, undefined values to prevent "NaN" in PDF
    if (amount === null || amount === undefined || isNaN(amount)) {
      return '$0';
    }
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  }

  private formatPercent(value: number): string {
    return `${value}%`;
  }

  // Professional header matching car PDF style
  private addHeader(pdf: jsPDF, scenarioName?: string): number {
    const margin = this.MARGIN_INCH;
    const headerHeight = 20;

    // Red background header bar
    pdf.setFillColor(220, 38, 38); // #dc2626 - website red
    pdf.rect(0, 0, this.PAGE_WIDTH, headerHeight, 'F');

    // Company name - white on red
    pdf.setTextColor(255, 255, 255);
    pdf.setFontSize(18);
    pdf.setFont('times', 'bold');
    pdf.text('Larry\'s Help', margin, 12);

    // Website URL - small, right aligned
    pdf.setFontSize(9);
    pdf.setFont('times', 'normal');
    pdf.text('LarrysHelp.org', this.PAGE_WIDTH - margin, 12, { align: 'right' });

    // Reset to black text
    pdf.setTextColor(0, 0, 0);

    // Scenario name as title (if provided)
    let yPos = headerHeight + 12;
    if (scenarioName) {
      pdf.setFontSize(16);
      pdf.setFont('times', 'bold');
      pdf.text(scenarioName, margin, yPos);
      yPos += 8;
    }

    // Subtitle
    pdf.setFontSize(11);
    pdf.setFont('times', 'normal');
    pdf.setTextColor(60, 60, 60); // Dark gray
    pdf.text('Housing Ownership vs Renting Analysis', margin, yPos);

    // Thin underline
    pdf.setDrawColor(220, 38, 38); // Match header red
    pdf.setLineWidth(0.3);
    pdf.line(margin, yPos + 2, this.PAGE_WIDTH - margin, yPos + 2);

    pdf.setTextColor(0, 0, 0); // Reset to black
    return yPos + 10; // Return starting Y position after header
  }

  // Professional footer
  private addFooter(pdf: jsPDF, pageNum: number, totalPages: number, reportDate: string): void {
    const margin = this.MARGIN_INCH;

    // Bottom border line
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.5);
    pdf.line(margin, this.PAGE_HEIGHT - margin + 5, this.PAGE_WIDTH - margin, this.PAGE_HEIGHT - margin + 5);

    // Prepared by (left)
    pdf.setFontSize(9);
    pdf.setFont('times', 'normal');
    pdf.setTextColor(0, 0, 0);
    pdf.text('Prepared by Larry\'s Help', margin, this.PAGE_HEIGHT - margin + 10);

    // Date (center)
    pdf.text(reportDate, this.PAGE_WIDTH / 2, this.PAGE_HEIGHT - margin + 10, { align: 'center' });

    // Page number (right)
    pdf.text(`Page ${pageNum} of ${totalPages}`, this.PAGE_WIDTH - margin, this.PAGE_HEIGHT - margin + 10, { align: 'right' });
  }

  // Professional section heading with underline (matching car PDF)
  private addSectionTitle(pdf: jsPDF, title: string, y: number): number {
    const margin = this.MARGIN_INCH;

    pdf.setTextColor(0, 0, 0);
    pdf.setFontSize(12);
    pdf.setFont('times', 'bold');
    pdf.text(title, margin, y);

    // Underline the section title
    pdf.setDrawColor(0, 0, 0);
    pdf.setLineWidth(0.3);
    const textWidth = pdf.getTextWidth(title);
    pdf.line(margin, y + 1, margin + textWidth, y + 1);

    pdf.setFont('times', 'normal');
    return y + 8;
  }

  // Add a table with professional financial statement styling (matching car PDF)
  private addTable(
    pdf: jsPDF,
    headers: string[],
    rows: (string | number)[][],
    yStart: number,
    columnWidths: number[]
  ): number {
    const margin = this.MARGIN_INCH;
    let y = yStart;

    // Header row with underline
    pdf.setFont('times', 'bold');
    pdf.setFontSize(10);
    let x = margin;
    headers.forEach((header, i) => {
      if (i === 0) {
        pdf.text(header, x, y);
      } else {
        pdf.text(header, x + columnWidths[i] - 5, y, { align: 'right' });
      }
      x += columnWidths[i];
    });

    // Underline header
    pdf.setLineWidth(0.3);
    pdf.line(margin, y + 1, margin + columnWidths.reduce((a, b) => a + b, 0), y + 1);
    y += 6;

    // Data rows
    pdf.setFont('times', 'normal');
    pdf.setFontSize(9);
    rows.forEach((row, rowIndex) => {
      x = margin;
      row.forEach((cell, i) => {
        const cellText = typeof cell === 'number' ? this.formatCurrency(cell) : cell;
        if (i === 0) {
          pdf.text(cellText, x, y);
        } else {
          pdf.text(cellText, x + columnWidths[i] - 5, y, { align: 'right' });
        }
        x += columnWidths[i];
      });
      y += 5;

      // Add line after last row (total)
      if (rowIndex === rows.length - 1) {
        pdf.setLineWidth(0.5);
        pdf.line(margin, y, margin + columnWidths.reduce((a, b) => a + b, 0), y);
        y += 2;
      }
    });

    return y + 5;
  }

  // Main PDF generation function - reads from TylerState like car calculator
  // eslint-disable-next-line @typescript-eslint/no-explicit-any, max-lines-per-function, max-statements, complexity
  async generateProfessionalPDF(
    _unusedInputs: PurchaseInputs | null,
    saveName: string,
    _unusedSummary: any | null,
    _unusedRentalAnalysis: any | null,
    _unusedEquivalentHomeValue: number | null
  ): Promise<void> {
    try {
      // Read all data from TylerState instead of props (like car calculator)
      const inputs = TylerState.housing.inputs;
      const purchaseSummary = TylerState.housing.outputs.purchase;
      const rentalAnalysis = TylerState.housing.outputs.rental;
      const equivalentHomeValue = purchaseSummary?.equivalentHomeValue || null;

      if (!inputs || !purchaseSummary || !rentalAnalysis) {
        console.error('Housing data not found in TylerState');
        return;
      }

      const pdf = new jsPDF('p', 'mm', 'a4');
      const margin = this.MARGIN_INCH;
      const contentWidth = this.PAGE_WIDTH - (2 * margin);
      const reportDate = new Date().toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });

      // Calculate total pages based on content
      let totalPages = 4; // Summary, Purchase Detail, Rental Detail, Disclaimer
      let currentPage = 1;

      // ===========================================
      // PAGE 1: SUMMARY
      // ===========================================
      let yPosition = this.addHeader(pdf, saveName || 'Housing Analysis');
      yPosition += 5;

      // Analysis period
      pdf.setFontSize(12);
      pdf.setFont('times', 'bold');
      pdf.text('Analysis Period:', margin, yPosition);
      pdf.setFont('times', 'normal');
      pdf.text(`${inputs.ownershipYears} years`, margin + 35, yPosition);
      yPosition += 8;

      pdf.setFont('times', 'bold');
      pdf.text('Property Value:', margin, yPosition);
      pdf.setFont('times', 'normal');
      pdf.text(this.formatCurrency(inputs.houseCost), margin + 35, yPosition);
      yPosition += 15;

      // Cost Comparison Summary
      yPosition = this.addSectionTitle(pdf, 'COST COMPARISON SUMMARY', yPosition);
      yPosition += 3;

      const summaryRows: (string | number)[][] = [
        ['Home Ownership', purchaseSummary.totalNetCost || purchaseSummary.costBreakdown?.netCost || 0, purchaseSummary.costPerMonth || 0],
        ['Renting (Equivalent)', rentalAnalysis.totalRentalPayments || 0, rentalAnalysis.costPerMonth || 0]
      ];

      yPosition = this.addTable(
        pdf,
        ['Option', 'Total Net Cost', 'Cost/Month'],
        summaryRows,
        yPosition,
        [70, 45, 44]
      );

      yPosition += 5;

      // Net Cash Flow Analysis
      yPosition = this.addSectionTitle(pdf, 'NET CASH FLOW ANALYSIS', yPosition);
      yPosition += 3;

      const cashFlowRows: (string | number)[][] = [
        ['Home ownership net position', purchaseSummary.netCashFlow || 0],
        ['Rental investment balance', rentalAnalysis.finalFundBalance || rentalAnalysis.finalInvestmentBalance || 0],
        ['Difference (rental - ownership)', (rentalAnalysis.finalFundBalance || 0) - (purchaseSummary.netCashFlow || 0)]
      ];

      yPosition = this.addTable(
        pdf,
        ['Description', 'Amount'],
        cashFlowRows,
        yPosition,
        [106, 53]
      );

      yPosition += 5;

      // Recommendation
      yPosition = this.addSectionTitle(pdf, 'RECOMMENDATION', yPosition);
      yPosition += 3;

      const winner = (rentalAnalysis.finalFundBalance || 0) > (purchaseSummary.netCashFlow || 0) ? 'Renting' : 'Home Ownership';
      const difference = Math.abs((rentalAnalysis.finalFundBalance || 0) - (purchaseSummary.netCashFlow || 0));

      pdf.setFont('times', 'normal');
      pdf.setFontSize(10);
      const recommendText = `Based on the ${inputs.ownershipYears}-year analysis period, ${winner} provides a better financial outcome by ${this.formatCurrency(difference)}. However, consider non-financial factors such as lifestyle preferences, job stability, and local market conditions.`;
      const lines = pdf.splitTextToSize(recommendText, contentWidth);
      lines.forEach((line: string) => {
        pdf.text(line, margin, yPosition);
        yPosition += 5;
      });

      // Notes section
      yPosition += 5;
      yPosition = this.addSectionTitle(pdf, 'NOTES AND ASSUMPTIONS', yPosition);
      yPosition += 3;

      pdf.setFontSize(9);
      const notes = [
        `Property value: ${this.formatCurrency(inputs.houseCost)}`,
        `Down payment: ${inputs.useDownpaymentPercent ? inputs.downpaymentPercent + '%' : this.formatCurrency(inputs.downpaymentAmount)}`,
        `Mortgage rate: ${inputs.interestRate}%`,
        `Mortgage term: ${inputs.mortgageYears} years`,
        `Property tax: ${inputs.propertyTaxPercent}% annually`,
        `Maintenance: ${inputs.maintenancePercent}% annually`,
        `Condo fees: ${this.formatCurrency(inputs.condoFees)}/month`,
        `Property appreciation: ${inputs.annualAppreciation}% annually`,
        `Monthly rent (comparison): ${this.formatCurrency(inputs.monthlyRent)}`,
        `Investment return rate: ${inputs.investmentReturnRate}% annually`,
        `Marginal tax rate: ${inputs.marginalTaxRate ?? (inputs.region === 'US' ? 22 : 40)}%`,
        `Capital gains tax on sale: ${inputs.taxesPercent > 0 ? inputs.taxesPercent + '% of the gain' : 'none (principal residence)'}`,
        `Tax on investment returns: ${inputs.investmentTaxRate > 0 ? inputs.investmentTaxRate + '% (assumes capital gains in a taxable account)' : (inputs.region === 'US' ? 'none (401(k)/IRA)' : 'none (registered account)')}`,
        `Rental inflation rate: ${inputs.rentalInflationRate}% annually`,
        `Renter invests ${inputs.renterSavingsDisciplinePercent ?? 100}% of the monthly saving from renting instead of owning`,
        `Cost inflation rate (owner costs): ${inputs.inflationRate}% annually`,
        `All costs include opportunity cost of capital`
      ];

      notes.forEach(note => {
        if (yPosition > this.PAGE_HEIGHT - 50) {
          this.addFooter(pdf, currentPage, totalPages, reportDate);
          pdf.addPage();
          currentPage++;
          yPosition = this.addHeader(pdf, saveName || 'Housing Analysis');
          yPosition = this.addSectionTitle(pdf, 'NOTES AND ASSUMPTIONS (continued)', yPosition);
          yPosition += 3;
        }

        pdf.text(`• ${note}`, margin, yPosition);
        yPosition += 5;
      });

      this.addFooter(pdf, currentPage, totalPages, reportDate);

      // ===========================================
      // PAGE 2: HOME OWNERSHIP DETAIL
      // ===========================================
      pdf.addPage();
      currentPage++;
      yPosition = this.addHeader(pdf, saveName || 'Housing Analysis');

      yPosition = this.addSectionTitle(pdf, 'HOME OWNERSHIP - DETAILED ANALYSIS', yPosition);
      yPosition += 5;

      // Financing Details
      yPosition = this.addSectionTitle(pdf, 'Financing Details', yPosition);
      yPosition += 3;

      const downPayment = inputs.useDownpaymentPercent
        ? inputs.houseCost * (inputs.downpaymentPercent / 100)
        : inputs.downpaymentAmount;

      const financingRows: (string | number)[][] = [
        ['Property value', inputs.houseCost],
        ['Down payment' + (inputs.useDownpaymentPercent ? ` (${inputs.downpaymentPercent}%)` : ''), downPayment],
        ['Closing costs' + ` (${inputs.closingCostsPercent}%)`, inputs.houseCost * (inputs.closingCostsPercent / 100)],
        ['Amount financed', purchaseSummary.totalMortgageValue || 0]
      ];

      yPosition = this.addTable(pdf, ['Description', 'Amount'], financingRows, yPosition, [106, 53]);

      // Mortgage Terms
      yPosition += 3;
      pdf.setFont('times', 'bold');
      pdf.setFontSize(10);
      pdf.text('Mortgage Terms:', margin, yPosition);
      yPosition += 5;

      pdf.setFont('times', 'normal');
      pdf.setFontSize(9);
      pdf.text(`Interest Rate: ${inputs.interestRate}%`, margin, yPosition);
      yPosition += 5;
      pdf.text(`Term: ${inputs.mortgageYears} years (${inputs.mortgageYears * 12} months)`, margin, yPosition);
      yPosition += 5;
      pdf.text(`Monthly Payment: ${this.formatCurrency(purchaseSummary.monthlyMortgagePayment || 0)}`, margin, yPosition);
      yPosition += 5;
      pdf.text(`Total Monthly Expense: ${this.formatCurrency(purchaseSummary.totalMonthlyExpense || 0)}`, margin, yPosition);
      yPosition += 10;

      // Cost Breakdown
      yPosition = this.addSectionTitle(pdf, 'Cost Breakdown Over ' + inputs.ownershipYears + ' Years', yPosition);
      yPosition += 3;

      const costBreakdown = purchaseSummary.costBreakdown || {};
      const breakdownRows: (string | number)[][] = [
        ['Mortgage interest', costBreakdown.interest || 0],
        ['Property tax', costBreakdown.propertyTax || 0],
        ['Maintenance', costBreakdown.maintenance || 0],
        ['Condo fees', costBreakdown.condoFees || 0],
        ['Selling costs', costBreakdown.sellingCosts || 0],
        ['Capital gains tax on sale', purchaseSummary.taxes || 0],
        ['Capital gains (after tax)', costBreakdown.capitalGains || 0],
        ['Net cost of ownership', costBreakdown.netCost || 0]
      ];

      yPosition = this.addTable(pdf, ['Item', 'Amount'], breakdownRows, yPosition, [106, 53]);

      // Year-by-year summary (first 10 years if more than 10)
      if (purchaseSummary.monthlyCashFlows && purchaseSummary.monthlyCashFlows.length > 0) {
        yPosition += 5;
        yPosition = this.addSectionTitle(pdf, 'Year-by-Year Summary (Annual)', yPosition);
        yPosition += 3;

        // Aggregate monthly data into yearly
        const yearlyData: any[] = [];
        for (let year = 1; year <= inputs.ownershipYears; year++) {
          const yearFlows = purchaseSummary.monthlyCashFlows.filter((f: any) => f.year === year);
          if (yearFlows.length > 0) {
            const totalExpense = yearFlows.reduce((sum: number, f: any) => sum + f.totalMonthlyExpense, 0);
            const principalPaid = yearFlows.reduce((sum: number, f: any) => sum + f.principalPaid, 0);
            const interestPaid = yearFlows.reduce((sum: number, f: any) => sum + f.interestPaid, 0);
            const endingBalance = yearFlows[yearFlows.length - 1].remainingBalance;

            yearlyData.push({
              year,
              totalExpense,
              principalPaid,
              interestPaid,
              endingBalance
            });
          }
        }

        // Show first 10 years or all if <= 10
        const yearsToShow = yearlyData.slice(0, Math.min(10, yearlyData.length));
        const yearlyRows: (string | number)[][] = yearsToShow.map(y => [
          `Year ${y.year}`,
          y.totalExpense,
          y.principalPaid,
          y.interestPaid,
          y.endingBalance
        ]);

        yPosition = this.addTable(
          pdf,
          ['Year', 'Total Expense', 'Principal', 'Interest', 'Balance'],
          yearlyRows,
          yPosition,
          [18, 32, 32, 32, 32]
        );

        if (yearlyData.length > 10) {
          pdf.setFontSize(8);
          pdf.text(`Note: Showing first 10 of ${yearlyData.length} years`, margin, yPosition);
          yPosition += 5;
        }
      }

      this.addFooter(pdf, currentPage, totalPages, reportDate);

      // ===========================================
      // PAGE 3: RENTAL ANALYSIS DETAIL
      // ===========================================
      pdf.addPage();
      currentPage++;
      yPosition = this.addHeader(pdf, saveName || 'Housing Analysis');

      yPosition = this.addSectionTitle(pdf, 'RENTAL ALTERNATIVE - DETAILED ANALYSIS', yPosition);
      yPosition += 5;

      // Rental Strategy
      yPosition = this.addSectionTitle(pdf, 'Rental Strategy', yPosition);
      yPosition += 3;

      pdf.setFont('times', 'normal');
      pdf.setFontSize(9);
      const strategyText = `Instead of purchasing a home, rent and invest the down payment plus the monthly difference between homeownership costs and rent. This analysis shows the final investment balance after ${inputs.ownershipYears} years.`;
      const strategyLines = pdf.splitTextToSize(strategyText, contentWidth);
      strategyLines.forEach((line: string) => {
        pdf.text(line, margin, yPosition);
        yPosition += 5;
      });

      yPosition += 5;

      // Rental Details
      yPosition = this.addSectionTitle(pdf, 'Rental Details', yPosition);
      yPosition += 3;

      const monthlySaving = purchaseSummary.monthlyMortgagePayment + (inputs.houseCost * (inputs.propertyTaxPercent + inputs.maintenancePercent) / 100 / 12) - (rentalAnalysis.equivalentMonthlyRent || inputs.monthlyRent);
      const savingsDiscipline = (inputs.renterSavingsDisciplinePercent ?? 100) / 100;
      const monthlyInvestmentContribution = monthlySaving > 0 ? monthlySaving * savingsDiscipline : monthlySaving;
      const rentalDetailRows: (string | number)[][] = [
        [inputs.analysisMode === 'rent-to-housing' ? 'Monthly rent' : 'Break-even monthly rent', rentalAnalysis.equivalentMonthlyRent || inputs.monthlyRent],
        ['Initial investment (down payment)', downPayment],
        ['Monthly investment contribution', monthlyInvestmentContribution],
        ['Investment return rate', inputs.investmentReturnRate + '%']
      ];

      yPosition = this.addTable(pdf, ['Description', 'Amount'], rentalDetailRows, yPosition, [106, 53]);

      yPosition += 5;

      // Rental Analysis Results
      yPosition = this.addSectionTitle(pdf, 'Results Summary', yPosition);
      yPosition += 3;

      const rentalResultsRows: (string | number)[][] = [
        ['Total rent paid', rentalAnalysis.totalRentalPayments || 0],
        ['Final investment balance', rentalAnalysis.finalFundBalance || rentalAnalysis.finalInvestmentBalance || 0],
        ['Net position (investment - rent)', (rentalAnalysis.finalFundBalance || 0)]
      ];

      yPosition = this.addTable(pdf, ['Item', 'Amount'], rentalResultsRows, yPosition, [106, 53]);

      // Year-by-year rental fund flows (first 10 years)
      if (rentalAnalysis.rentalFundFlows && rentalAnalysis.rentalFundFlows.length > 0) {
        yPosition += 5;
        yPosition = this.addSectionTitle(pdf, 'Year-by-Year Summary (Annual)', yPosition);
        yPosition += 3;

        // Aggregate monthly data into yearly
        const yearlyData: any[] = [];
        for (let year = 1; year <= inputs.ownershipYears; year++) {
          const yearFlows = rentalAnalysis.rentalFundFlows.filter((f: any) => f.year === year);
          if (yearFlows.length > 0) {
            const totalRent = yearFlows.reduce((sum: number, f: any) => sum + f.rentalPayment, 0);
            const totalInvestmentIncome = yearFlows.reduce((sum: number, f: any) => sum + f.investmentIncome, 0);
            const totalFunding = yearFlows.reduce((sum: number, f: any) => sum + f.monthlyFunding, 0);
            const endingBalance = yearFlows[yearFlows.length - 1].closingBalance;

            yearlyData.push({
              year,
              totalRent,
              totalFunding,
              totalInvestmentIncome,
              endingBalance
            });
          }
        }

        // Show first 10 years or all if <= 10
        const yearsToShow = yearlyData.slice(0, Math.min(10, yearlyData.length));
        const yearlyRows: (string | number)[][] = yearsToShow.map(y => [
          `Year ${y.year}`,
          y.totalRent,
          y.totalFunding,
          y.totalInvestmentIncome,
          y.endingBalance
        ]);

        yPosition = this.addTable(
          pdf,
          ['Year', 'Rent Paid', 'Invested', 'Returns', 'Balance'],
          yearlyRows,
          yPosition,
          [18, 28, 28, 28, 38]
        );

        if (yearlyData.length > 10) {
          pdf.setFontSize(8);
          pdf.text(`Note: Showing first 10 of ${yearlyData.length} years`, margin, yPosition);
          yPosition += 5;
        }
      }

      this.addFooter(pdf, currentPage, totalPages, reportDate);

      // ===========================================
      // PAGE 4: DISCLAIMER
      // ===========================================
      pdf.addPage();
      currentPage++;
      yPosition = this.addHeader(pdf, saveName || 'Housing Analysis');

      // DISCLAIMER SECTION
      yPosition = this.addSectionTitle(pdf, 'IMPORTANT DISCLAIMER', yPosition);
      yPosition += 3;

      const disclaimerText = [
        'This housing analysis is provided for educational and informational purposes only. The calculations and projections are estimates based on the information you provided and certain assumptions about future events.',
        '',
        'Key Limitations:',
        '• Real estate market conditions, interest rates, and property values may vary significantly',
        '• Property appreciation rates are estimates and actual values may differ substantially',
        '• Operating costs, taxes, and maintenance expenses vary by location and property type',
        '• Investment returns and rental market conditions are subject to change',
        '• This analysis does not constitute financial or real estate advice',
        '',
        'Recommendations:',
        '• Consult with qualified real estate professionals and financial advisors',
        '• Consider your personal financial situation, goals, and risk tolerance',
        '• Review local market conditions and recent comparable sales',
        '• Factor in additional costs such as moving expenses, renovations, and utilities',
        '• Evaluate the impact of changing life circumstances on your housing needs',
        '',
        'Larry\'s Help provides financial planning tools to help you make informed decisions, but the ultimate responsibility for housing and investment decisions rests with you.'
      ];

      pdf.setFontSize(9);
      pdf.setFont('times', 'normal');

      disclaimerText.forEach((text) => {
        if (yPosition > this.PAGE_HEIGHT - 60) {
          this.addFooter(pdf, currentPage, totalPages, reportDate);
          pdf.addPage();
          currentPage++;
          totalPages++; // Adjust if needed
          yPosition = this.addHeader(pdf, saveName || 'Housing Analysis');
          yPosition = this.addSectionTitle(pdf, 'IMPORTANT DISCLAIMER (continued)', yPosition);
          yPosition += 3;
        }

        if (text === '') {
          yPosition += 4;
        } else {
          const maxWidth = contentWidth - 10;
          const lines = pdf.splitTextToSize(text, maxWidth);
          lines.forEach((line: string) => {
            if (yPosition > this.PAGE_HEIGHT - 60) {
              this.addFooter(pdf, currentPage, totalPages, reportDate);
              pdf.addPage();
              currentPage++;
              totalPages++;
              yPosition = this.addHeader(pdf, saveName || 'Housing Analysis');
              yPosition = this.addSectionTitle(pdf, 'IMPORTANT DISCLAIMER (continued)', yPosition);
              yPosition += 3;
            }
            pdf.text(line, margin, yPosition);
            yPosition += 6;
          });
        }
      });

      // Add final footer
      this.addFooter(pdf, currentPage, totalPages, reportDate);

      // Save the PDF
      pdf.save(`housing-analysis-${saveName || 'scenario'}.pdf`);
      
    } catch (error) {
      console.error('Error generating Housing PDF:', error);
    }
  }
}

export default HousingPDFService; 